import React, { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import ModernAdminLayout from '../components/admin/ModernAdminLayout';
import ModernAdminHeader from '../components/admin/ModernAdminHeader';
import ModernAdminContent from '../components/admin/ModernAdminContent';
import ModernDashboardOverview from '../components/admin/ModernDashboardOverview';
import ModernReservationsTab from '../components/admin/ModernReservationsTab';
import ModernEventsTab from '../components/admin/ModernEventsTab';
import ModernClientsTab from '../components/admin/ModernClientsTab';
import ModernMediaLibraryTab from '../components/admin/ModernMediaLibraryTab';
import ModernAdminModals from '../components/admin/ModernAdminModals';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || (
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? 'http://localhost:3000'
    : 'https://crapule-bxl-production.up.railway.app'
);
const API = `${BACKEND_URL}/api`;
const adminAPI = axios.create({ baseURL: API });
adminAPI.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const mediaSrc = (u) => (u && u.startsWith('http') ? u : `${BACKEND_URL}${u || ''}`);

const Admin = () => {
  const { isAuthenticated, loading: authLoading, logout, user } = useAuth();
  const [reservations, setReservations] = useState([]);
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState(null);
  const [events, setEvents] = useState([]);
  const [nextEvent, setNextEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [copiedEmails, setCopiedEmails] = useState(false);
  const [updatingEvent, setUpdatingEvent] = useState(false);
  
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [isMenuModalOpen, setIsMenuModalOpen] = useState(false);
  const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);
  const [isDeleteClientModalOpen, setIsDeleteClientModalOpen] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [mediaItems, setMediaItems] = useState([]);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [mediaActiveUpdating, setMediaActiveUpdating] = useState(false);
  const [mediaActiveMessage, setMediaActiveMessage] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [isFeaturedModalOpen, setIsFeaturedModalOpen] = useState(false);
  const [featuredItems, setFeaturedItems] = useState([]);
  const [featuredLoading, setFeaturedLoading] = useState(false);

  // Global media library (in Media tab)
  const [libraryList, setLibraryList] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [libraryFilter, setLibraryFilter] = useState('all');
  const [libraryQuery, setLibraryQuery] = useState('');
  const [librarySort, setLibrarySort] = useState('-createdAt');
  const [librarySelectedIds, setLibrarySelectedIds] = useState([]);

  // Unified media selection modal
  const [isSelectionModalOpen, setIsSelectionModalOpen] = useState(false);
  const [selectionMode, setSelectionMode] = useState('featured'); // 'featured' | 'event'
  const [selectionEventId, setSelectionEventId] = useState('');
  const [selectionTab, setSelectionTab] = useState('uploaded'); // 'uploaded' | 'upload'
  const [selectionFilter, setSelectionFilter] = useState('all'); // all | image | video | document
  const [selectionQuery, setSelectionQuery] = useState('');
  const [selectionList, setSelectionList] = useState([]);
  const [selectionLoading, setSelectionLoading] = useState(false);
  const [selectionSelectedIds, setSelectionSelectedIds] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', guests: '2', menuChoice: 'meat', eventVolume: '2', status: 'confirmed'
  });

  const [eventFormData, setEventFormData] = useState({
    volume: '', theme: '', date: '', time: '19H - 00H', price: '', status: 'upcoming', description: '', reservationsOpen: false
  });

  const [menuFormData, setMenuFormData] = useState({ dishes: [] });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [reservationsRes, statsRes, clientsRes, eventsRes] = await Promise.all([
        adminAPI.get(`/reservations`),
        adminAPI.get(`/events/stats`),
        adminAPI.get(`/clients`),
        adminAPI.get(`/events`)
      ]);
      setReservations(reservationsRes.data.reservations);
      setStats(statsRes.data);
      setClients(clientsRes.data);
      setEvents(eventsRes.data.events || []);
      setNextEvent(eventsRes.data.nextEvent);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);


  const toggleReservations = async (eventId, currentStatus) => {
    setUpdatingEvent(true);
    try {
      const newStatus = !currentStatus;
      // When opening reservations, set status to 'available'
      // When closing reservations, set status to 'upcoming'
      await adminAPI.put(`/events/${eventId}`, { 
        reservationsOpen: newStatus,
        status: newStatus ? 'available' : 'upcoming'
      });
      fetchData();
    } catch (error) {
      console.error('Error updating event:', error);
    } finally {
      setUpdatingEvent(false);
    }
  };

  const handleAddReservation = async (e) => {
    e.preventDefault();
    try {
      await adminAPI.post(`/reservations`, {
        name: formData.name, email: formData.email, phone: formData.phone,
        guests: parseInt(formData.guests), menuChoice: formData.menuChoice, eventVolume: parseInt(formData.eventVolume)
      });
      setIsAddModalOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      alert('Erreur lors de la création');
    }
  };

  const handleEditReservation = async (e) => {
    e.preventDefault();
    try {
      await adminAPI.put(`/reservations/${selectedReservation.id}`, {
        name: formData.name, email: formData.email, phone: formData.phone,
        guests: parseInt(formData.guests), menuChoice: formData.menuChoice,
        eventVolume: parseInt(formData.eventVolume), status: formData.status
      });
      setIsEditModalOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      alert('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteReservation = async () => {
    try {
      await adminAPI.delete(`/reservations/${selectedReservation.id}`);
      setIsDeleteModalOpen(false);
      fetchData();
    } catch (error) {
      alert('Erreur lors de la suppression');
    }
  };

  const handleSaveEvent = async (e) => {
    e.preventDefault();
    try {
      const eventData = {
        volume: parseInt(eventFormData.volume), theme: eventFormData.theme, date: eventFormData.date,
        time: eventFormData.time, price: eventFormData.price ? parseInt(eventFormData.price) : null,
        status: eventFormData.status, description: eventFormData.description, reservationsOpen: eventFormData.reservationsOpen
      };
      if (selectedEvent) {
        await adminAPI.put(`/events/${selectedEvent.id}`, eventData);
      } else {
        await adminAPI.post(`/events`, eventData);
      }
      setIsEventModalOpen(false);
      resetEventForm();
      fetchData();
    } catch (error) {
      alert('Erreur lors de la sauvegarde');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Supprimer cet événement ?')) return;
    try {
      await adminAPI.delete(`/events/${eventId}`);
      fetchData();
    } catch (error) {
      alert('Erreur lors de la suppression');
    }
  };

  const handleUploadMedia = async (files) => {
    if (!selectedEvent || !files || files.length === 0) return;
    setUploadingMedia(true);
    try {
      for (const file of files) {
        const form = new FormData();
        form.append('eventId', selectedEvent.id);
        form.append('file', file);
        form.append('visible', 'true');
        await adminAPI.post(`/media/upload`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      const res = await adminAPI.get(`/media/${selectedEvent.id}`);
      setMediaItems(res.data.media || []);
    } catch (error) {
      alert('Erreur lors du téléversement');
    } finally {
      setUploadingMedia(false);
    }
  };

  const updateMedia = async (mediaId, fields) => {
    try {
      const res = await adminAPI.put(`/media/${mediaId}`, fields);
      setMediaItems(prev => prev.map(m => m.id === mediaId ? res.data : m));
    } catch (error) {
      alert('Erreur lors de la mise à jour du média');
    }
  };

  const deleteMedia = async (mediaId) => {
    try {
      await adminAPI.delete(`/media/${mediaId}`);
      setMediaItems(prev => prev.filter(m => m.id !== mediaId));
    } catch (error) {
      alert('Erreur lors de la suppression du média');
    }
  };

  const moveMedia = async (index, direction) => {
    if (!selectedEvent) return;
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= mediaItems.length) return;
    const items = [...mediaItems];
    const tmp = items[index];
    items[index] = items[newIndex];
    items[newIndex] = tmp;
    const reordered = items.map((item, i) => ({ id: item.id, order: i }));
    try {
      await adminAPI.post(`/media/reorder/${selectedEvent.id}`, reordered);
      setMediaItems(items.map((item, i) => ({ ...item, order: i })));
    } catch (error) {
      alert('Erreur lors du réordonnancement');
    }
  };

  const openDeleteClientModal = (client) => {
    setSelectedClient(client);
    setIsDeleteClientModalOpen(true);
  };

  const handleDeleteClient = async () => {
    if (!selectedClient) return;
    
    try {
      await adminAPI.delete(`/clients/${encodeURIComponent(selectedClient.email)}`);
      setIsDeleteClientModalOpen(false);
      setSelectedClient(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting client:', error);
      alert('Erreur lors de la suppression du client');
    }
  };

  const handleSaveMenu = async (e) => {
    e.preventDefault();
    try {
      await adminAPI.post(`/menus`, { eventId: selectedEvent.id, dishes: menuFormData.dishes });
      setIsMenuModalOpen(false);
      fetchData();
    } catch (error) {
      alert('Erreur lors de la sauvegarde du menu');
    }
  };

  const openEventModal = (event = null) => {
    if (event) {
      setSelectedEvent(event);
      setEventFormData({
        volume: event.volume.toString(), theme: event.theme, date: event.date,
        time: event.time || '19H - 00H', price: event.price?.toString() || '',
        status: event.status, description: event.description || '', reservationsOpen: event.reservationsOpen || false
      });
    } else {
      setSelectedEvent(null);
      resetEventForm();
    }
    setIsEventModalOpen(true);
  };

  const openMenuModal = async (event) => {
    setSelectedEvent(event);
    try {
    const res = await adminAPI.get(`/menus/${event.id}`);
      setMenuFormData({ dishes: res.data.menu?.dishes || [] });
    } catch (error) {
      setMenuFormData({ dishes: [] });
    }
    setIsMenuModalOpen(true);
  };

  const openMediaModal = async (event) => {
    setSelectedEvent(event);
    try {
    const res = await adminAPI.get(`/media/${event.id}`);
      setMediaItems(res.data.media || []);
    } catch (error) {
      setMediaItems([]);
    }
    setIsMediaModalOpen(true);
  };

  const fetchFeaturedMedia = async () => {
    setFeaturedLoading(true);
    try {
      const res = await adminAPI.get(`/featured-media`);
      const items = (res.data.items || []).map(i => ({
        id: i.featured.id,
        mediaId: i.featured.mediaId,
        order: i.featured.order,
        priority: i.featured.priority,
        durationSeconds: i.featured.durationSeconds,
        visible: i.featured.visible,
        media: i.media,
      }));
      setFeaturedItems(items);
    } catch (error) {
      setFeaturedItems([]);
    } finally {
      setFeaturedLoading(false);
    }
  };

  const openFeaturedModal = async () => {
    await fetchFeaturedMedia();
    setIsFeaturedModalOpen(true);
  };

  const fetchLibrary = async () => {
    setLibraryLoading(true);
    try {
      const params = [];
      if (libraryFilter !== 'all') params.push(`type=${libraryFilter}`);
      if (libraryQuery) params.push(`q=${encodeURIComponent(libraryQuery)}`);
      params.push(`sort=${encodeURIComponent(librarySort || '-createdAt')}`);
      const res = await adminAPI.get(`/media/all${params.length ? `?${params.join('&')}` : ''}`);
      let items = res.data.media || [];
      if (!items || items.length === 0) {
        try {
          const eventsRes = await adminAPI.get(`/events`);
          const eventIds = (eventsRes.data.events || []).map(ev => ev.id);
          const ids = [...eventIds, 'global'];
          const medias = await Promise.all(ids.map(id => adminAPI.get(`/media/${id}`).catch(() => ({ data: { media: [] } }))));
          items = medias.flatMap(m => m.data.media || []);
        } catch (aggErr) {
          items = [];
        }
      }
      setLibraryList(items);
    } catch (error) {
      // Fallback on error
      try {
        const eventsRes = await adminAPI.get(`/events`);
        const eventIds = (eventsRes.data.events || []).map(ev => ev.id);
        const ids = [...eventIds, 'global'];
        const medias = await Promise.all(ids.map(id => adminAPI.get(`/media/${id}`).catch(() => ({ data: { media: [] } }))));
        const items = medias.flatMap(m => m.data.media || []);
        setLibraryList(items);
      } catch (aggErr) {
        setLibraryList([]);
      }
    } finally {
      setLibraryLoading(false);
    }
  };

  const updateLibraryMedia = async (mediaId, fields) => {
    try {
      const res = await adminAPI.put(`/media/${mediaId}`, fields);
      setLibraryList(prev => prev.map(m => m.id === mediaId ? res.data : m));
    } catch (error) {
      alert('Erreur lors de la mise à jour des métadonnées');
    }
  };

  const addSelectedToFeatured = async () => {
    if (selectionSelectedIds.length === 0) return;
    try {
      for (const mid of selectionSelectedIds) {
        await adminAPI.post(`/featured-media`, { mediaId: mid, priority: 0, durationSeconds: 5, visible: true });
      }
      await fetchFeaturedMedia();
      setIsSelectionModalOpen(false);
      setSelectionSelectedIds([]);
    } catch (error) {
      alert('Erreur lors de l\'ajout au featured');
    }
  };

  const deleteFeaturedItem = async (fid) => {
    try {
      await adminAPI.delete(`/featured-media/${fid}`);
      setFeaturedItems(prev => prev.filter(f => f.id !== fid));
    } catch (error) {
      alert('Erreur lors de la suppression');
    }
  };

  const updateFeaturedItem = async (fid, fields) => {
    try {
      const res = await adminAPI.put(`/featured-media/${fid}`, fields);
      setFeaturedItems(prev => prev.map(f => f.id === fid ? { ...f, ...fields } : f));
    } catch (error) {
      alert('Erreur lors de la mise à jour');
    }
  };

  const moveFeatured = async (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= featuredItems.length) return;
    const items = [...featuredItems];
    const tmp = items[index];
    items[index] = items[newIndex];
    items[newIndex] = tmp;
    const reordered = items.map((item, i) => ({ id: item.id, order: i }));
    try {
      await adminAPI.post(`/featured-media/reorder`, reordered);
      setFeaturedItems(items.map((item, i) => ({ ...item, order: i })));
    } catch (error) {
      alert('Erreur lors du réordonnancement');
    }
  };

  const openSelectionModal = (mode = 'featured', eventId = '') => {
    setSelectionMode(mode);
    setSelectionEventId(eventId || (selectedEvent?.id || ''));
    setSelectionTab('uploaded');
    setSelectionFilter('all');
    setSelectionQuery('');
    setSelectionSelectedIds([]);
    setIsSelectionModalOpen(true);
    loadSelectionList();
  };

  const loadSelectionList = async () => {
    setSelectionLoading(true);
    try {
      const params = [];
      if (selectionFilter !== 'all') params.push(`type=${selectionFilter}`);
      if (selectionQuery) params.push(`q=${encodeURIComponent(selectionQuery)}`);
      const res = await adminAPI.get(`/media/all${params.length ? `?${params.join('&')}` : ''}`);
      let items = res.data.media || [];
      if (!items || items.length === 0) {
        // Fallback: aggregate from each event + global
        try {
          const eventsRes = await adminAPI.get(`/events`);
          const eventIds = (eventsRes.data.events || []).map(ev => ev.id);
          const ids = [...eventIds, 'global'];
          const medias = await Promise.all(ids.map(id => adminAPI.get(`/media/${id}`).catch(() => ({ data: { media: [] } }))));
          items = medias.flatMap(m => m.data.media || []);
        } catch (aggErr) {
          items = [];
        }
      }
      setSelectionList(items);
    } catch (error) {
      try {
        const eventsRes = await adminAPI.get(`/events`);
        const eventIds = (eventsRes.data.events || []).map(ev => ev.id);
        const ids = [...eventIds, 'global'];
        const medias = await Promise.all(ids.map(id => adminAPI.get(`/media/${id}`).catch(() => ({ data: { media: [] } }))));
        const items = medias.flatMap(m => m.data.media || []);
        setSelectionList(items);
      } catch (aggErr) {
        setSelectionList([]);
      }
    } finally {
      setSelectionLoading(false);
    }
  };

  const toggleSelection = (mid) => {
    setSelectionSelectedIds(prev => prev.includes(mid) ? prev.filter(id => id !== mid) : [...prev, mid]);
  };

  const addSelectedToEvent = async () => {
    if (!selectionEventId || selectionSelectedIds.length === 0) return;
    try {
      for (const mid of selectionSelectedIds) {
        await adminAPI.post(`/media/copy`, { sourceMediaId: mid, targetEventId: selectionEventId });
      }
      if (selectedEvent && selectedEvent.id === selectionEventId) {
        const res = await adminAPI.get(`/media/${selectedEvent.id}`);
        setMediaItems(res.data.media || []);
      }
      setIsSelectionModalOpen(false);
    } catch (error) {
      alert('Erreur lors de l\'ajout au média de l\'événement');
    }
  };

  const toggleEventMediaActive = async () => {
    if (!selectedEvent) return;
    setMediaActiveUpdating(true);
    try {
      const nextActive = !selectedEvent.mediaActive;
      await adminAPI.put(`/events/${selectedEvent.id}`, { mediaActive: nextActive });
      setSelectedEvent(prev => ({ ...prev, mediaActive: nextActive }));
      setEvents(prev => prev.map(e => e.id === selectedEvent.id ? { ...e, mediaActive: nextActive } : e));
      setNextEvent(prev => (prev && prev.id === selectedEvent.id) ? { ...prev, mediaActive: nextActive } : prev);
      setMediaActiveMessage(nextActive ? 'Médias activés pour cet événement' : 'Médias désactivés pour cet événement');
      setTimeout(() => setMediaActiveMessage(''), 2000);
    } catch (error) {
      setMediaActiveMessage('Erreur lors de la mise à jour du statut des médias');
    } finally {
      setMediaActiveUpdating(false);
    }
  };

  const openEditModal = (reservation) => {
    setSelectedReservation(reservation);
    setFormData({
      name: reservation.name, email: reservation.email, phone: reservation.phone,
      guests: reservation.guests.toString(), menuChoice: reservation.menuChoice,
      eventVolume: reservation.eventVolume.toString(), status: reservation.status
    });
    setIsEditModalOpen(true);
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', phone: '', guests: '2', menuChoice: 'meat', eventVolume: '2', status: 'confirmed' });
    setSelectedReservation(null);
  };

  const resetEventForm = () => {
    setEventFormData({ volume: '', theme: '', date: '', time: '19H - 00H', price: '', status: 'upcoming', description: '', reservationsOpen: false });
    setSelectedEvent(null);
  };

  const copyEmailsToClipboard = async () => {
    if (!clients) return;
    try {
      await navigator.clipboard.writeText(clients.emailsFormatted);
      setCopiedEmails(true);
      setTimeout(() => setCopiedEmails(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const addDish = () => {
    setMenuFormData(prev => ({
      dishes: [...prev.dishes, { name: '', type: 'main', vegetarian: false, ingredients: [], description: '' }]
    }));
  };

  const updateDish = (index, field, value) => {
    setMenuFormData(prev => ({
      dishes: prev.dishes.map((dish, i) => i === index ? { ...dish, [field]: value } : dish)
    }));
  };

  const removeDish = (index) => {
    setMenuFormData(prev => ({ dishes: prev.dishes.filter((_, i) => i !== index) }));
  };

  const addIngredient = (dishIndex) => {
    setMenuFormData(prev => ({
      dishes: prev.dishes.map((dish, i) => 
        i === dishIndex 
          ? { ...dish, ingredients: [...(dish.ingredients || []), ''] }
          : dish
      )
    }));
  };

  const updateIngredient = (dishIndex, ingredientIndex, value) => {
    setMenuFormData(prev => ({
      dishes: prev.dishes.map((dish, i) => 
        i === dishIndex 
          ? { 
              ...dish, 
              ingredients: dish.ingredients.map((ing, j) => j === ingredientIndex ? value : ing)
            }
          : dish
      )
    }));
  };

  const removeIngredient = (dishIndex, ingredientIndex) => {
    setMenuFormData(prev => ({
      dishes: prev.dishes.map((dish, i) => 
        i === dishIndex 
          ? { 
              ...dish, 
              ingredients: dish.ingredients.filter((_, j) => j !== ingredientIndex)
            }
          : dish
      )
    }));
  };

  const filteredReservations = reservations.filter(res => {
    const matchesSearch = res.name.toLowerCase().includes(searchTerm.toLowerCase()) || res.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || res.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const styles = {
      confirmed: 'bg-green-500/20 text-green-400', pending: 'bg-yellow-500/20 text-yellow-400',
      cancelled: 'bg-red-500/20 text-red-400'
    };
    const labels = { confirmed: 'Confirmé', pending: 'En attente', cancelled: 'Annulé' };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || ''}`}>{labels[status] || status}</span>;
  };

  const getEventStatusBadge = (status) => {
    const styles = {
      past: 'bg-gray-500/20 text-gray-400', upcoming: 'bg-yellow-500/20 text-yellow-400',
      available: 'bg-green-500/20 text-green-400', sold_out: 'bg-red-500/20 text-red-400'
    };
    const labels = { past: 'Terminé', upcoming: 'À venir', available: 'Ouvert', sold_out: 'Complet' };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || ''}`}>{labels[status] || status}</span>;
  };

  if (authLoading) return <div className="flex justify-center items-center min-h-screen text-white bg-black"><div className="text-gray-400">Chargement...</div></div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  return <ModernAdminLayout />;
};

export default Admin;
